import React, { useCallback, useMemo, useState } from 'react';
import { reportsAPI } from '../../services/api';
import useReport from './shared/useReport';
import KpiStrip from './shared/KpiStrip';
import ReportTable from './shared/ReportTable';
import ReportPrintLayout from './shared/ReportPrintLayout';
import PrintButton from './shared/PrintButton';
import useDrill from './shared/useDrill';
import { formatAge } from '../../utils/age';

// One national ID sitting on more than one patient record. A CNIC gets here two ways: it is the
// patient's own CNIC, or it was typed into the guardian field on a dependent's record. Both count
// as the same CNIC being in use, so the report treats them as one list and then says, per patient,
// how that patient relates to whoever actually holds the ID.
//
// There is no date filter on purpose. A CNIC only looks shared when every record carrying it is in
// scope, and the registrations are often years apart, so any window would hide half of a pair.

const SIGNALS = {
  DUPLICATE: {
    label: 'Duplicate registration',
    chip: 'bg-rose-100 text-rose-700',
    // Two patient files, one CNIC, both claiming to be the person. One of them needs merging.
    match: (g) => Number(g.selfUses) >= 2,
  },
  HOLDER_NOT_REGISTERED: {
    label: 'Holder not registered',
    chip: 'bg-amber-100 text-amber-800',
    match: (g) => Number(g.selfUses) === 0,
  },
  GUARDIAN_LINK: {
    label: 'Family link',
    chip: 'bg-emerald-100 text-emerald-700',
    match: (g) => Number(g.guardianUses) >= 1,
  },
};

const FILTERS = [
  { key: 'all', label: 'All', match: () => true },
  { key: 'DUPLICATE', label: 'Duplicate registrations', match: SIGNALS.DUPLICATE.match },
  { key: 'GUARDIAN_LINK', label: 'Guardian links', match: SIGNALS.GUARDIAN_LINK.match },
  { key: 'HOLDER_NOT_REGISTERED', label: 'Holder not registered', match: SIGNALS.HOLDER_NOT_REGISTERED.match },
];

// guardianRelationship is recorded looking outward from the patient: it means "the holder of this
// CNIC is my FATHER". The report asks the opposite question, so it is inverted here. Gender is the
// patient's own, which is why one enum value maps to two labels.
const INVERSE_RELATION = {
  FATHER: { MALE: 'Son', FEMALE: 'Daughter', fallback: 'Child' },
  MOTHER: { MALE: 'Son', FEMALE: 'Daughter', fallback: 'Child' },
  GRANDFATHER: { MALE: 'Grandson', FEMALE: 'Granddaughter', fallback: 'Grandchild' },
  GRANDMOTHER: { MALE: 'Grandson', FEMALE: 'Granddaughter', fallback: 'Grandchild' },
  UNCLE: { MALE: 'Nephew', FEMALE: 'Niece', fallback: 'Nephew or niece' },
  AUNT: { MALE: 'Nephew', FEMALE: 'Niece', fallback: 'Nephew or niece' },
  BROTHER: { MALE: 'Brother', FEMALE: 'Sister', fallback: 'Sibling' },
  SISTER: { MALE: 'Brother', FEMALE: 'Sister', fallback: 'Sibling' },
  GUARDIAN: { fallback: 'Ward' },
  OTHER: { fallback: 'Other relative' },
};

const relationToHolder = (row) => {
  if (row.usedAs === 'SELF') return 'CNIC holder';
  const map = INVERSE_RELATION[row.guardianRelationship];
  if (!map) return 'Not recorded';
  return map[row.gender] || map.fallback;
};

const signalOf = (category) => SIGNALS[category] || SIGNALS.GUARDIAN_LINK;

const Chip = ({ className, children }) => (
  <span className={`px-2 py-0.5 rounded-md text-xs font-medium whitespace-nowrap ${className}`}>{children}</span>
);

const GROUP_COLUMNS = [
  { key: 'cnic', label: 'CNIC', render: (r) => <span className="font-mono">{r.cnic}</span> },
  {
    key: 'category', label: 'Signal',
    render: (r) => <Chip className={signalOf(r.category).chip}>{signalOf(r.category).label}</Chip>,
    csv: (r) => signalOf(r.category).label,
  },
  {
    key: 'holderNames', label: 'CNIC Holder', wrap: true,
    render: (r) => (r.holderNames
      ? r.holderNames
      : <span className="text-amber-700">Not registered as a patient</span>),
    csv: (r) => r.holderNames || 'Not registered as a patient',
  },
  {
    key: 'holderMrns', label: 'Holder MRN',
    render: (r) => <span className="font-mono text-xs">{r.holderMrns || '—'}</span>,
  },
  { key: 'selfUses', label: 'Registered as Self', align: 'right', wrap: true },
  { key: 'guardianUses', label: 'Used as Guardian', align: 'right', wrap: true },
  { key: 'patients', label: 'Patients', align: 'right' },
];

const ROW_COLUMNS = [
  { key: 'cnic', label: 'CNIC', render: (r) => <span className="font-mono">{r.cnic}</span> },
  {
    key: 'usedAs', label: 'Used As',
    render: (r) => (
      <Chip className={r.usedAs === 'SELF' ? 'bg-emerald-100 text-emerald-700' : 'bg-sky-100 text-sky-700'}>
        {r.usedAs === 'SELF' ? 'Own CNIC' : 'Guardian CNIC'}
      </Chip>
    ),
    csv: (r) => (r.usedAs === 'SELF' ? 'Own CNIC' : 'Guardian CNIC'),
  },
  { key: 'mrn', label: 'MRN', render: (r) => <span className="font-mono text-xs">{r.mrn}</span> },
  { key: 'patient', label: 'Patient', wrap: true },
  {
    key: 'dateOfBirth', label: 'Age / Gender', wrap: true,
    render: (r) => `${formatAge(r.dateOfBirth)}, ${r.gender || 'not recorded'}`.toLowerCase(),
    csv: (r) => `${formatAge(r.dateOfBirth)}, ${r.gender || 'not recorded'}`,
    sortValue: (r) => r.dateOfBirth,
  },
  {
    key: 'guardianRelationship', label: 'Relation to CNIC Holder', wrap: true,
    render: (r) => {
      const relation = relationToHolder(r);
      return relation === 'Not recorded'
        ? <span className="text-gray-400">Not recorded</span>
        : <span className="font-medium text-gray-900">{relation}</span>;
    },
    csv: relationToHolder,
    sortValue: relationToHolder,
  },
  {
    key: 'holderNames', label: 'CNIC Holder', wrap: true,
    render: (r) => (r.usedAs === 'SELF' ? <span className="text-gray-400">This patient</span> : (r.holderNames
      ? r.holderNames
      : <span className="text-amber-700">Not registered</span>)),
    csv: (r) => (r.usedAs === 'SELF' ? 'This patient' : r.holderNames || 'Not registered'),
  },
  {
    // The name typed into the guardian field. Worth showing next to the real holder: when the two
    // disagree, the CNIC was very likely entered against the wrong person.
    key: 'guardianName', label: 'Guardian Named', wrap: true,
    render: (r) => r.guardianName || '—',
  },
  {
    key: 'familyHead', label: 'Family Head', wrap: true,
    render: (r) => r.familyHead || '—',
  },
  { key: 'phone', label: 'Phone', render: (r) => <span className="font-mono text-xs">{r.phone}</span> },
  {
    key: 'status', label: 'Status',
    render: (r) => (r.status === 'ACTIVE'
      ? <span className="text-gray-500">Active</span>
      : <Chip className="bg-gray-200 text-gray-700">{r.status}</Chip>),
  },
  { key: 'registeredOn', label: 'Registered' },
];

// Third section. A guardian CNIC names a person and so does the guardian name field; when the two
// disagree, the CNIC is the harder fact, so the record is probably carrying somebody else's ID.
// Only records whose guardian CNIC belongs to a registered patient can be checked at all.
const VERDICTS = {
  MISMATCH: { label: 'Name mismatch', chip: 'bg-rose-100 text-rose-700' },
  PARTIAL: { label: 'Partial match', chip: 'bg-amber-100 text-amber-800' },
  NO_NAME: { label: 'No guardian name', chip: 'bg-gray-100 text-gray-600' },
};

const verdictOf = (row) => VERDICTS[row.verdict] || VERDICTS.NO_NAME;

// GUARDIAN → "Guardian", GRANDFATHER → "Grandfather".
const relationLabel = (code) =>
  code ? code.charAt(0) + code.slice(1).toLowerCase() : 'Not recorded';

const GUARDIAN_COLUMNS = [
  {
    key: 'verdict', label: 'Finding',
    render: (r) => <Chip className={verdictOf(r).chip}>{verdictOf(r).label}</Chip>,
    csv: (r) => verdictOf(r).label,
  },
  { key: 'mrn', label: 'MRN', render: (r) => <span className="font-mono text-xs">{r.mrn}</span> },
  { key: 'patient', label: 'Patient', wrap: true },
  {
    key: 'dateOfBirth', label: 'Age / Gender', wrap: true,
    render: (r) => `${formatAge(r.dateOfBirth)}, ${(r.gender || 'not recorded').toLowerCase()}`,
    csv: (r) => `${formatAge(r.dateOfBirth)}, ${r.gender || 'not recorded'}`,
  },
  {
    key: 'guardianRelationship', label: 'Recorded As', wrap: true,
    render: (r) => relationLabel(r.guardianRelationship),
    csv: (r) => relationLabel(r.guardianRelationship),
  },
  {
    key: 'guardianCnic', label: 'Guardian CNIC',
    render: (r) => <span className="font-mono">{r.guardianCnic}</span>,
  },
  {
    // The two names sit next to each other on purpose: this pair IS the finding.
    key: 'guardianNamed', label: 'Guardian Named On Record', wrap: true,
    render: (r) => (r.guardianNamed
      ? <span className="font-medium text-rose-700">{r.guardianNamed}</span>
      : <span className="text-gray-400">not recorded</span>),
    csv: (r) => r.guardianNamed || '',
  },
  {
    key: 'holderNames', label: 'CNIC Actually Belongs To', wrap: true,
    render: (r) => <span className="font-medium text-gray-900">{r.holderNames}</span>,
  },
  {
    key: 'holderMrns', label: 'Holder MRN',
    render: (r) => <span className="font-mono text-xs">{r.holderMrns}</span>,
  },
  { key: 'familyHead', label: 'Family Head', wrap: true, render: (r) => r.familyHead || '—' },
  { key: 'registeredOn', label: 'Registered' },
];

const SharedCnicReport = () => {
  const drill = useDrill();
  const [filter, setFilter] = useState('all');

  const fetcher = useCallback(() => reportsAPI.getSharedCnic(), []);
  const { data, loading } = useReport(fetcher);

  const groups = useMemo(() => data?.groups || [], [data]);
  const allRows = useMemo(() => data?.rows || [], [data]);
  const guardianChecks = useMemo(() => data?.guardianChecks || [], [data]);
  const guardianSummary = data?.guardianSummary || {};

  const counts = useMemo(
    () => FILTERS.reduce((acc, f) => ({ ...acc, [f.key]: groups.filter(f.match).length }), {}),
    [groups]
  );

  // The pills narrow both tables together, so the detail always describes the CNICs shown above it.
  const { visibleGroups, visibleRows } = useMemo(() => {
    const match = (FILTERS.find((f) => f.key === filter) || FILTERS[0]).match;
    const kept = groups.filter(match);
    const keptCnics = new Set(kept.map((g) => g.cnic));
    return { visibleGroups: kept, visibleRows: allRows.filter((r) => keptCnics.has(r.cnic)) };
  }, [groups, allRows, filter]);

  const k = data?.kpis || {};
  const kpis = [
    { label: 'Shared CNICs', value: k.sharedCnics ?? 0, sub: 'Used on more than one record' },
    { label: 'Patients Involved', value: k.patientsInvolved ?? 0, sub: 'Files carrying a shared CNIC' },
    {
      label: 'Duplicate Registrations',
      value: k.duplicateRegistrations ?? 0,
      sub: 'One CNIC, two patient files',
      // The only category that means a record is wrong rather than merely related.
      tone: Number(k.duplicateRegistrations || 0) > 0 ? 'alert' : undefined,
    },
    { label: 'Guardian Links', value: k.guardianLinkedPatients ?? 0, sub: 'Dependents naming a guardian CNIC' },
    {
      label: 'Holder Not Registered',
      value: k.holderNotRegistered ?? 0,
      sub: 'Guardian never registered as a patient',
    },
    {
      label: 'Guardian Name Wrong',
      value: k.guardianNameMismatch ?? 0,
      sub: 'Named guardian is not the CNIC holder',
      tone: Number(k.guardianNameMismatch || 0) > 0 ? 'alert' : undefined,
    },
  ];

  const asOf = data?.range?.to || 'today';

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 flex-wrap">
        <p className="text-sm text-gray-500 flex-1 min-w-[300px]">
          As of {asOf}. The whole patient base is scanned, with no date range, because a CNIC only
          reads as shared when every record carrying it is included.
        </p>
        <PrintButton />
      </div>

      <KpiStrip items={kpis} loading={loading} />

      <div className="bg-white rounded-xl shadow p-1 inline-flex flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
              filter === f.key ? 'bg-navy-800 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {f.label}
            <span className={`ml-1.5 ${filter === f.key ? 'text-navy-200' : 'text-gray-400'}`}>
              {counts[f.key] ?? 0}
            </span>
          </button>
        ))}
      </div>

      <ReportTable
        onRowClick={(r) => drill.open({ type: 'cnic', value: r.cnic })}
        title="Shared CNICs"
        columns={GROUP_COLUMNS}
        rows={visibleGroups}
        csvName={`shared-cnic-summary-${asOf}`}
        emptyText="No CNIC is used on more than one patient record"
        footnote="A CNIC counts as shared whether it appears as a patient's own CNIC or as a guardian CNIC on someone else's record."
      />

      <ReportTable
        onRowClick={(r) => drill.open({ type: 'patient', mrn: r.mrn })}
        title="Patients on a shared CNIC"
        columns={ROW_COLUMNS}
        rows={visibleRows}
        csvName={`shared-cnic-patients-${asOf}`}
        emptyText="No patients to show"
        footnote={'Relation reads from the patient to the CNIC holder, inverted from the guardian relationship on the record. "Guardian Named" is the name typed into the record; where it disagrees with the CNIC holder, the ID was probably entered against the wrong person.'}
      />

      <div className="pt-2">
        <p className="text-sm text-gray-500">
          {guardianSummary.checked ?? 0} records name a guardian CNIC that belongs to a registered
          patient, so the recorded guardian name can be checked against them.{' '}
          {guardianSummary.matched ?? 0} agree and are left out below. A further{' '}
          {guardianSummary.notCheckable ?? 0} records name a guardian CNIC nobody on file holds;
          those cannot be checked either way, so they are not counted as clean.
        </p>
      </div>

      <ReportTable
        onRowClick={(r) => drill.open({ type: 'patient', mrn: r.mrn })}
        title="Guardian name does not match the CNIC holder"
        columns={GUARDIAN_COLUMNS}
        rows={guardianChecks}
        csvName={`guardian-name-mismatch-${asOf}`}
        emptyText="Every checkable guardian name agrees with the CNIC holder"
        footnote="A name counts as matching if it is the same once punctuation and word order are ignored, so 'Waheed Wasim' matches 'Wasim Waheed'. Sharing only part of a name is reported as a partial match, which is usually a spelling slip. Where a CNIC has more than one holder, naming any one of them counts as a match."
      />

      <ReportPrintLayout
        title="Shared CNIC Report"
        subtitle={`Whole patient base, as of ${asOf}`}
        kpis={kpis}
        tables={[
          { title: 'Shared CNICs', columns: GROUP_COLUMNS, rows: visibleGroups },
          { title: 'Patients on a shared CNIC', columns: ROW_COLUMNS, rows: visibleRows },
          {
            title: 'Guardian name does not match the CNIC holder',
            columns: GUARDIAN_COLUMNS,
            rows: guardianChecks,
          },
        ]}
      />

      {drill.node}
    </div>
  );
};

export default SharedCnicReport;
